import { css, html } from 'react-strict-dom';

import { Color } from '../constants';

type Typeface =
  | 'headline'
  | 'body.regular'
  | 'body.medium'
  | 'body.small.regular'
  | 'body.small.medium'
  | 'caption.semiBold'
  | 'caption.medium';

type Props = {
  as?:
    | 'b'
    | 'blockquote'
    | 'div'
    | 'em'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'label'
    | 'li'
    | 'p'
    | 'span'
    | 'strong';
  bold?: boolean;
  children: React.ReactNode;
  color?: keyof typeof Color;
  lineThrough?: boolean;
  noSelect?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  typeface?: Typeface;
  underline?: boolean;
  italic?: boolean;
  style?: css.StyleXStyles;
};

export default function Text({
  as = 'span',
  bold,
  children,
  color,
  lineThrough,
  noSelect,
  textAlign,
  typeface = 'body.regular',
  underline,
  italic,
  style,
}: Props) {
  const Tag = html[as];

  return (
    <Tag
      style={[
        styles.base,
        typefaceStyles[typeface],
        bold && styles.bold,
        lineThrough && styles.lineThrough,
        noSelect && styles.noSelect,
        underline && styles.underline,
        italic && styles.italic,
        color && styles.color(Color[color]),
        textAlign && styles.textAlignStyle(textAlign),
        style,
      ]}
    >
      {children}
    </Tag>
  );
}

const _tf = css.create({
  headline: {
    fontSize: 20,
    lineHeight: 1.4,
    fontWeight: '600',
  },
  bodyRegular: {
    fontSize: 16,
    lineHeight: 1.4,
    fontWeight: '400',
  },
  bodyMedium: {
    fontSize: 16,
    lineHeight: 1.4,
    fontWeight: '500',
  },
  bodySmallRegular: {
    fontSize: 14,
    lineHeight: 1.4,
    fontWeight: '400',
  },
  bodySmallMedium: {
    fontSize: 14,
    lineHeight: 1.4,
    fontWeight: '500',
  },
  captionSemiBold: {
    fontSize: 12,
    lineHeight: 1.4,
    fontWeight: '600',
  },
  captionMedium: {
    fontSize: 12,
    lineHeight: 1.4,
    fontWeight: '500',
  },
});

const typefaceStyles: Record<Typeface, typeof _tf[keyof typeof _tf]> = {
  headline: _tf.headline,
  'body.regular': _tf.bodyRegular,
  'body.medium': _tf.bodyMedium,
  'body.small.regular': _tf.bodySmallRegular,
  'body.small.medium': _tf.bodySmallMedium,
  'caption.semiBold': _tf.captionSemiBold,
  'caption.medium': _tf.captionMedium,
};

const styles = css.create({
  base: {
    fontFamily: 'Inter, sans-serif',
    margin: 0,
    padding: 0,
  },
  bold: {
    fontWeight: '600',
  },
  lineThrough: {
    textDecorationLine: 'line-through',
  },
  noSelect: {
    userSelect: 'none',
  },
  underline: {
    textDecorationLine: 'underline',
  },
  italic: {
    fontStyle: 'italic',
  },
  color: (value: string) => ({
    color: value,
  }),
  textAlignStyle: (align: 'left' | 'center' | 'right') => ({
    textAlign: align,
  }),
});
